import React, { useState } from "react";
import { ServiceMemberDto } from "../../../pages/types/types";

interface YouthProfileExtensionProps {
  member: ServiceMemberDto;
}

type ContactSummary = {
  contactType?: string;
  ContactType?: string;
  value?: string;
  Value?: string;
};

type PrimaryContactSummary = {
  partyId?: number;
  PartyId?: number;
  displayName?: string;
  DisplayName?: string;
  contactSummary?: ContactSummary[];
  ContactSummary?: ContactSummary[];
};

type YouthProfileSummary = {
  schoolName?: string;
  SchoolName?: string;
  schoolYear?: string;
  SchoolYear?: string;
  isLookedAfterChild?: boolean | null;
  IsLookedAfterChild?: boolean | null;
  knownToSocialServices?: boolean | null;
  KnownToSocialServices?: boolean | null;
  hasEHCP?: boolean | null;
  HasEHCP?: boolean | null;
  underCAMHS?: boolean | null;
  UnderCAMHS?: boolean | null;
  medicalInformation?: string | null;
  MedicalInformation?: string | null;
  additionalNeeds?: string | null;
  AdditionalNeeds?: string | null;
  dietaryRequirements?: string | null;
  DietaryRequirements?: string | null;
  gpName?: string | null;
  GPName?: string | null;
  gpContactNumber?: string | null;
  GPContactNumber?: string | null;
};

type ConsentSummary = {
  consentTypeCode?: string;
  ConsentTypeCode?: string;
  consentTypeName?: string;
  ConsentTypeName?: string;
  granted?: boolean | null;
  Granted?: boolean | null;
};

const getValue = <T,>(
  source: Record<string, unknown> | null | undefined,
  camelKey: string,
  pascalKey: string,
): T | undefined => {
  if (!source) return undefined;
  return (source[camelKey] ?? source[pascalKey]) as T | undefined;
};

const boolLabel = (value?: boolean | null) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "Not recorded";
};

const textValue = (value?: string | null) => value || "-";

const hasText = (value?: string | null) => Boolean(value?.trim());

const getText = (camelValue?: string | null, pascalValue?: string | null) =>
  camelValue ?? pascalValue ?? "";

const yesNoItems = (
  youthProfileSummary: YouthProfileSummary | null | undefined,
) =>
  [
    {
      label: "Looked after",
      value:
        youthProfileSummary?.isLookedAfterChild ??
        youthProfileSummary?.IsLookedAfterChild,
    },
    {
      label: "Social services",
      value:
        youthProfileSummary?.knownToSocialServices ??
        youthProfileSummary?.KnownToSocialServices,
    },
    {
      label: "EHCP",
      value: youthProfileSummary?.hasEHCP ?? youthProfileSummary?.HasEHCP,
    },
    {
      label: "CAMHS",
      value: youthProfileSummary?.underCAMHS ?? youthProfileSummary?.UnderCAMHS,
    },
  ].filter((item) => item.value !== null && item.value !== undefined);

const formatContacts = (contacts?: ContactSummary[]) => {
  if (!contacts?.length) return "-";

  return contacts
    .map((contact) => {
      const type = contact.contactType ?? contact.ContactType ?? "Contact";
      const value = contact.value ?? contact.Value ?? "";
      return value ? `${type}: ${value}` : "";
    })
    .filter(Boolean)
    .join(", ") || "-";
};

export default function YouthProfileExtension({
  member,
}: YouthProfileExtensionProps) {
  const [showFullDetails, setShowFullDetails] = useState(false);
  const profileExtensions = member.profileExtensions ?? {};
  const youthProfile =
    member.youthProfile ??
    getValue<YouthProfileSummary>(
      profileExtensions,
      "youthProfile",
      "YouthProfile",
    );
  const primaryContact =
    member.primaryContact ??
    getValue<PrimaryContactSummary>(
      profileExtensions,
      "primaryContact",
      "PrimaryContact",
    );
  const primaryContactSummary = primaryContact as PrimaryContactSummary | null | undefined;
  const primaryContactContacts =
    primaryContactSummary?.contactSummary ?? primaryContactSummary?.ContactSummary;
  const youthProfileSummary = youthProfile as YouthProfileSummary | null | undefined;
  const consents: ConsentSummary[] = (
    member.consents ??
    getValue<ConsentSummary[]>(profileExtensions, "consents", "Consents") ??
    []
  ) as ConsentSummary[];
  const permissionToLeave = consents.find((consent) => {
    const code = consent.consentTypeCode ?? consent.ConsentTypeCode ?? "";
    const name = consent.consentTypeName ?? consent.ConsentTypeName ?? "";
    return (
      code.toUpperCase() === "LEAVE_INDEPENDENTLY" ||
      name.toLowerCase() === "leave consent"
    );
  });
  const schoolName = getText(
    youthProfileSummary?.schoolName,
    youthProfileSummary?.SchoolName,
  );
  const schoolYear = getText(
    youthProfileSummary?.schoolYear,
    youthProfileSummary?.SchoolYear,
  );
  const gpName = getText(youthProfileSummary?.gpName, youthProfileSummary?.GPName);
  const gpContactNumber = getText(
    youthProfileSummary?.gpContactNumber,
    youthProfileSummary?.GPContactNumber,
  );
  const dietaryRequirements = getText(
    youthProfileSummary?.dietaryRequirements,
    youthProfileSummary?.DietaryRequirements,
  );
  const medicalInformation = getText(
    youthProfileSummary?.medicalInformation,
    youthProfileSummary?.MedicalInformation,
  );
  const additionalNeeds = getText(
    youthProfileSummary?.additionalNeeds,
    youthProfileSummary?.AdditionalNeeds,
  );
  const profileFlags = yesNoItems(youthProfileSummary);
  const hasProfile =
    hasText(schoolName) ||
    hasText(schoolYear) ||
    hasText(gpName) ||
    hasText(gpContactNumber) ||
    hasText(dietaryRequirements) ||
    hasText(medicalInformation) ||
    hasText(additionalNeeds) ||
    profileFlags.length > 0;
  const primaryContactName = textValue(
    primaryContactSummary?.displayName ?? primaryContactSummary?.DisplayName,
  );
  const permissionToLeaveLabel = boolLabel(
    permissionToLeave?.granted ?? permissionToLeave?.Granted,
  );

  return (
    <div className="service-member-extension service-member-extension-minimal">
      <div className="service-extension-minimal-header">
        <span>Profile Snapshot</span>
        <button
          className="service-extension-toggle"
          type="button"
          onClick={() => setShowFullDetails((current) => !current)}
        >
          {showFullDetails ? "Hide full details" : "Show full details"}
        </button>
      </div>

      <dl className="service-extension-snapshot-list">
        <div>
          <dt>Primary contact</dt>
          <dd>
            <strong>{primaryContactName}</strong>
            <span>{formatContacts(primaryContactContacts)}</span>
          </dd>
        </div>
        <div>
          <dt>Permission to leave</dt>
          <dd>{permissionToLeaveLabel}</dd>
        </div>
        <div>
          <dt>Allergies</dt>
          <dd>{textValue(dietaryRequirements)}</dd>
        </div>
      </dl>

      {showFullDetails && hasProfile ? (
        <>
          <dl className="service-extension-minimal-list">
            <div>
              <dt>School</dt>
              <dd>{textValue(schoolName)}</dd>
            </div>
            <div>
              <dt>Year</dt>
              <dd>{textValue(schoolYear)}</dd>
            </div>
            {hasText(dietaryRequirements) && (
              <div>
                <dt>Dietary</dt>
                <dd>{dietaryRequirements}</dd>
              </div>
            )}
            {hasText(medicalInformation) && (
              <div>
                <dt>Medical</dt>
                <dd>{medicalInformation}</dd>
              </div>
            )}
            {hasText(additionalNeeds) && (
              <div>
                <dt>Needs</dt>
                <dd>{additionalNeeds}</dd>
              </div>
            )}
            {(hasText(gpName) || hasText(gpContactNumber)) && (
              <div>
                <dt>GP</dt>
                <dd>{[gpName, gpContactNumber].filter(Boolean).join(" | ")}</dd>
              </div>
            )}
          </dl>

          {profileFlags.length > 0 && (
            <div className="service-extension-minimal-flags">
              {profileFlags.map((item) => (
                <span key={item.label}>
                  {item.label}: {boolLabel(item.value)}
                </span>
              ))}
            </div>
          )}
        </>
      ) : showFullDetails ? (
        <div className="service-member-extension-empty">
          No youth profile data recorded.
        </div>
      ) : null}
      {!showFullDetails && !hasProfile && (
        <div className="service-extension-minimal-contact">
          No additional profile details recorded.
        </div>
      )}
    </div>
  );
}
